import { FileDown, Pencil, Trash2, Bell, Check, X, Zap, CornerDownLeft } from "lucide-react";
import { isoToEU } from "../../../lib/format";
import { downloadAgreementPdf } from "../../../lib/downloadPdf";
import { SourcePill, PaymentPill, StatusSelect } from "./BookingBadges";

/**
 * Reservations table. Pure — parent owns bookings state, filters, and
 * action handlers. Renders headers + rows + empty/loading states.
 */
export default function BookingsTable({
  bookings, loading, onStatusChange, onEdit, onDelete, onReviewModification,
  onRequestDecision, onHandover, onReturn,
}) {
  return (
    <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10 bg-[#FAF9F6] dark:bg-[#111827]">
            <tr>
              <th className="py-3 px-4 font-medium">Booking</th>
              <th className="py-3 px-4 font-medium">Customer</th>
              <th className="py-3 px-4 font-medium">Vehicle</th>
              <th className="py-3 px-4 font-medium">Dates</th>
              <th className="py-3 px-4 font-medium">Total</th>
              <th className="py-3 px-4 font-medium">Source</th>
              <th className="py-3 px-4 font-medium">Payment</th>
              <th className="py-3 px-4 font-medium">Status</th>
              <th className="py-3 px-4 font-medium">PDF</th>
              <th className="py-3 px-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="py-10 text-center text-neutral-500 dark:text-neutral-400">Loading…</td></tr>
            )}
            {!loading && bookings.length === 0 && (
              <tr><td colSpan={10} className="py-10 text-center text-neutral-500 dark:text-neutral-400">No bookings match.</td></tr>
            )}
            {!loading && bookings.map((b) => (
              <BookingRow
                key={b.id}
                b={b}
                onStatusChange={onStatusChange}
                onEdit={onEdit}
                onDelete={onDelete}
                onReviewModification={onReviewModification}
                onRequestDecision={onRequestDecision}
                onHandover={onHandover}
                onReturn={onReturn}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * v4.2.7 — Total / Deposit / Balance in one compact block so an operator
 * can tell at a glance what was actually collected vs what is due at
 * pickup. `paid_amount` is the source of truth for money received.
 */
function MoneyBreakdown({ b }) {
  const total = Number(b.total || 0);
  const deposit = Number(
    b.paid_amount != null ? b.paid_amount : (b.advance_deposit || 0),
  );
  const balance = Math.max(0, total - deposit);
  const settled = deposit >= total && total > 0;
  const paid = deposit > 0;

  return (
    <div className="leading-tight">
      <div className="font-medium">€{total.toFixed(2)}</div>
      <div className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 flex items-center gap-1.5">
        <span>Dep. €{deposit.toFixed(2)}</span>
        <span
          className={`px-1.5 py-[1px] rounded text-[9px] uppercase tracking-wider font-semibold ${
            settled
              ? "bg-emerald-100 text-emerald-800"
              : paid
                ? "bg-amber-100 text-amber-800"
                : "bg-neutral-200 text-neutral-700 dark:bg-white/10 dark:text-neutral-300"
          }`}
        >
          {settled ? "Settled" : paid ? "Paid" : "Unpaid"}
        </span>
      </div>
      <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
        Bal. €{balance.toFixed(2)}
      </div>
    </div>
  );
}

function BookingRow({ b, onStatusChange, onEdit, onDelete, onReviewModification, onRequestDecision, onHandover, onReturn }) {
  // v4.7.8 — staff/admin get full discretion: the Handover quick action is
  // available on any confirmed (or modification-requested) booking, no
  // matter how far in the future the pickup date is.
  const dueForHandover = ["confirmed", "modification_requested"].includes(b.status);
  return (
    <tr
      className="border-b border-black/5 dark:border-white/5 hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
      data-testid={`row-${b.id}`}
    >
      <td className="py-4 px-4 font-mono text-xs min-h-[60px]">{b.id.slice(0, 8).toUpperCase()}</td>
      <td className="py-4 px-4">
        <div className="font-semibold text-slate-900 dark:text-white text-sm">{b.full_name}</div>
        <div className="text-xs text-neutral-500 dark:text-neutral-400">{b.user_email}</div>
      </td>
      <td className="py-4 px-4 font-medium text-slate-800 dark:text-neutral-200 text-sm">
        {b.vehicle_snapshot?.brand} {b.vehicle_snapshot?.name}
      </td>
      <td className="py-4 px-4 text-xs font-medium text-slate-700 dark:text-neutral-300">
        <div>{isoToEU(b.pickup_date)}</div>
        <div className="text-neutral-500 dark:text-neutral-400">→ {isoToEU(b.return_date)}</div>
      </td>
      <td className="py-4 px-4 tabular-nums" data-testid={`money-${b.id}`}>
        <MoneyBreakdown b={b} />
      </td>
      <td className="py-4 px-4">
        <SourcePill source={b.source} testId={`source-${b.id}`} />
      </td>
      <td className="py-4 px-4">
        <PaymentPill paymentStatus={b.payment_status} paymentMethod={b.payment_method} testId={`pay-${b.id}`} />
      </td>
      <td className="py-4 px-4">
        <StatusSelect
          status={b.status}
          onChange={(next) => onStatusChange(b, next)}
          testId={`status-${b.id}`}
        />
        {b.status === "active" && (
          <div
            className="mt-1 font-bold text-xs text-slate-800 dark:text-slate-100"
            data-testid={`status-plate-${b.id}`}
          >
            {b.assigned_plate || "No plate assigned"}
          </div>
        )}
        {b.status === "confirmed" && !b.handed_over_at && (
          <div
            className="mt-1 text-[10px] font-medium text-amber-700 dark:text-amber-400"
            data-testid={`status-pending-handover-${b.id}`}
          >
            Confirmed (Pending Handover)
          </div>
        )}
      </td>
      <td className="py-4 px-4">
        <button
          type="button"
          onClick={() => downloadAgreementPdf(b.id)}
          className="inline-flex items-center gap-1 text-xs text-neutral-600 dark:text-neutral-300 hover:text-black dark:hover:text-white"
          data-testid={`pdf-${b.id}`}
        >
          <FileDown size={12} /> PDF
        </button>
      </td>
      <td className="py-4 px-4">
        <div className="flex justify-end gap-1">
          {dueForHandover && (
            <button
              type="button"
              onClick={() => onHandover?.(b)}
              title="Express Check-In — hand the car over to the driver"
              data-testid={`handover-${b.id}`}
              className="px-2 py-1.5 rounded bg-sky-600 text-white text-xs inline-flex items-center gap-1 hover:bg-sky-700 transition-colors whitespace-nowrap"
            >
              <Zap size={13} /> Handover
            </button>
          )}
          {b.status === "active" && (
            <>
              <button
                type="button"
                onClick={() => onReturn?.(b)}
                title="Record return — close the rental"
                data-testid={`return-${b.id}`}
                className="px-2 py-1.5 rounded bg-emerald-600 text-white text-xs inline-flex items-center gap-1 hover:bg-emerald-700 transition-colors whitespace-nowrap"
              >
                <CornerDownLeft size={13} /> Return
              </button>
              <span
                className="px-2 py-1.5 rounded bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-[11px] inline-flex items-center gap-1"
                title={`Handed over${b.handed_over_by ? ` by ${b.handed_over_by}` : ""}${b.handed_over_at ? ` at ${new Date(b.handed_over_at).toLocaleString()}` : ""}${b.assigned_plate ? ` · ${b.assigned_plate}` : ""}`}
                data-testid={`handed-over-${b.id}`}
              >
                <Zap size={11} /> {b.assigned_plate || "Handed over"}
              </span>
            </>
          )}
          {b.status === "pending" && (
            <>
              <button
                type="button"
                onClick={() => onRequestDecision?.(b, "approve")}
                title="Approve booking request"
                data-testid={`approve-request-${b.id}`}
                className="px-2 py-1.5 rounded bg-emerald-600 text-white text-xs inline-flex items-center gap-1 hover:bg-emerald-700 transition-colors"
              >
                <Check size={13} /> Approve
              </button>
              <button
                type="button"
                onClick={() => onRequestDecision?.(b, "decline")}
                title="Decline booking request"
                data-testid={`decline-request-${b.id}`}
                className="px-2 py-1.5 rounded border border-rose-300 text-rose-700 text-xs inline-flex items-center gap-1 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-900/30 transition-colors"
              >
                <X size={13} /> Decline
              </button>
            </>
          )}
          {b.status === "modification_requested" && (
            <button
              type="button"
              onClick={() => onReviewModification?.(b)}
              aria-label={`Review modification for ${b.id.slice(0, 8)}`}
              title="Review modification request"
              data-testid={`review-modification-${b.id}`}
              className="p-1.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30"
            >
              <Bell size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit(b)}
            aria-label={`Edit booking ${b.id.slice(0, 8)}`}
            title="Edit booking"
            data-testid={`edit-booking-${b.id}`}
            className="p-1.5 rounded border border-transparent hover:border-black/15 dark:hover:border-white/15 text-neutral-600 dark:text-neutral-300"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(b)}
            aria-label={`Delete booking ${b.id.slice(0, 8)}`}
            title="Delete booking"
            data-testid={`delete-booking-${b.id}`}
            className="p-1.5 rounded border border-transparent hover:border-rose-300 dark:hover:border-rose-900 text-rose-600 dark:text-rose-400"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}
